<script setup lang="ts">
import {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    TagsInput,
    TagsInputItem,
    TagsInputItemDelete,
    TagsInputItemText,
    ZhFieldError,
    ZhFormField,
    ZhFormLayout,
    ZhSavePreview,
} from '@zerohuman/ui'
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { api, type Member, type MemberToolGap } from '@/api'
import HolderToolGaps from '@/components/HolderToolGaps.vue'
import { useFieldValidation } from '@/composables/useFieldValidation'
import { useFormDraft } from '@/composables/useFormDraft'
import { requiredName } from '@/lib/fieldRules'
import { buildRolePreview } from '@/lib/formPreview'
import type { HolderToolGapsEntry, HolderToolGapsState } from '@/lib/holderToolGaps'
import { memberLabel } from '@/lib/memberLabel'

/**
 * Every control here is the kit's (#1225): the fields are the kit `Input` under kit `Label`s, the holder picker
 * the kit `Select`, the chips the kit `TagsInput`, on a kit surface. The screen states no colour, radius, border
 * or fill of its own.
 */
const router = useRouter()
const name = ref('')
const slug = ref('')
const members = ref<Member[]>([])
const holderIds = ref<string[]>([])
/**
 * The holder picker holds no member: picking one makes them a holder at once, so it always reads its placeholder.
 * Held as a ref so the kit Select is bound (controlled) and goes back to empty after every pick.
 */
const pickMember = ref('')
const error = ref<string | null>(null)
const saving = ref(false)
/** Name is required (the field's own rule), checked while typing (#1757). */
const fields = useFieldValidation(
    computed(() => ({ name: name.value })),
    { name: requiredName('role') },
    { idPrefix: 'create-role' },
)
/**
 * #1763: the name, slug and holders are kept in this browser until the role is created, so leaving (Cancel, another
 * link, a reload, a closed tab) loses nothing. Nothing of it is sent until Create role is confirmed.
 */
const draft = useFormDraft('create-role', { name, slug, holderIds })
// A restored draft is not a change made on this page: its fields show no error until changed or left.
fields.reset()

const holders = computed(() => holderIds.value.flatMap(id => members.value.filter(member => member.id === id)))
const availableMembers = computed(() => {
    const held = new Set(holderIds.value)
    return members.value.filter(member => !held.has(member.id))
})

/**
 * #637: for each picked holder, the tools their roles' tasks need that no binding of theirs reaches, read from the
 * API as soon as they are picked (before the role exists) and dropped when they are taken back off. While a read is
 * out the list says it is checking; a failed read says so with Try again, and leaves the member picked.
 */
const gapsByMember = ref<Record<string, { gaps: MemberToolGap[]; state?: HolderToolGapsState }>>({})
const holderGaps = computed<HolderToolGapsEntry[]>(() =>
    holders.value.map(member => ({
        memberId: member.id,
        memberName: memberLabel(member),
        gaps: gapsByMember.value[member.id]?.gaps ?? [],
        state: gapsByMember.value[member.id]?.state,
    })),
)

async function loadHolderGaps(memberId: string): Promise<void> {
    gapsByMember.value = { ...gapsByMember.value, [memberId]: { gaps: [], state: 'checking' } }
    let read: { gaps: MemberToolGap[]; state?: HolderToolGapsState }
    try {
        read = { gaps: await api.memberToolGaps(memberId) }
    } catch {
        read = { gaps: [], state: 'failed' }
    }
    if (holderIds.value.includes(memberId)) {
        gapsByMember.value = { ...gapsByMember.value, [memberId]: read }
    }
}

watch(
    holderIds,
    ids => {
        const kept = Object.fromEntries(Object.entries(gapsByMember.value).filter(([id]) => ids.includes(id)))
        if (Object.keys(kept).length !== Object.keys(gapsByMember.value).length) gapsByMember.value = kept
        for (const id of ids) {
            if (!(id in gapsByMember.value)) void loadHolderGaps(id)
        }
    },
    { immediate: true },
)

onMounted(async () => {
    try {
        members.value = await api.members()
        // A restored holder who is no longer a member could not be shown or added: they are dropped.
        const kept = holderIds.value.filter(id => members.value.some(member => member.id === id))
        if (kept.length !== holderIds.value.length) holderIds.value = kept
    } catch {
        members.value = []
    }
})

function memberName(memberId: unknown): string {
    const member = members.value.find(candidate => candidate.id === memberId)
    return member ? memberLabel(member) : String(memberId)
}

/** The choice arrives from the kit Select's model update; the picker is never left holding it. */
function addMember(choice: unknown): void {
    const memberId = choice === null || choice === undefined ? '' : String(choice)
    pickMember.value = ''
    if (memberId && !holderIds.value.includes(memberId)) {
        holderIds.value = [...holderIds.value, memberId]
    }
}

function removeMember(memberId: unknown): void {
    holderIds.value = holderIds.value.filter(id => id !== String(memberId))
}

/**
 * The save preview (#1756): pressing Create role names the role and who will hold it, in plain words and never by
 * its slug, and nothing is created until that is confirmed. Any change, a holder added or removed included, hides it.
 */
const preview = ref<string[] | null>(null)

/** Validate first (CTO decision 19 on #1756): an invalid Name shows its error, takes focus and opens no preview. */
function openPreview(): void {
    if (fields.check() !== null) return
    preview.value = buildRolePreview({
        name: name.value,
        holderNames: holders.value.map(member => memberLabel(member)),
    })
}

watch([name, slug, holderIds], () => {
    preview.value = null
})

async function confirmPreview(): Promise<void> {
    preview.value = null
    await create()
}

async function create(): Promise<void> {
    if (fields.check() !== null) return
    saving.value = true
    error.value = null
    let roleId: string | null = null
    try {
        const role = await api.createRole({ name: name.value, slug: slug.value || undefined })
        roleId = role.id
        // The role exists from here on, holders or not: the next visit starts blank rather than creating it twice.
        draft.clear()
        for (const memberId of holderIds.value) {
            await api.addRoleMember(role.id, memberId)
        }
        await router.push(`/roles/${role.id}`)
    } catch (caught) {
        error.value = caught instanceof Error ? `Could not create (${caught.message}).` : 'Could not create.'
        // The role itself already exists once it has an id, so the form must not be resubmitted:
        // its detail page is the one place that shows which members actually made it on.
        if (roleId) {
            await router.push(`/roles/${roleId}`)
        }
    } finally {
        saving.value = false
    }
}
</script>

<template>
	<section class="w-full space-y-6 p-8">
		<p class="text-muted-foreground text-sm">
			<RouterLink class="underline" to="/roles">Roles</RouterLink>
		</p>
		<!-- #1756: the shared form shell, one help line on every field, and a plain-words preview before saving. -->
		<ZhFormLayout
			title="Create role"
			purpose="Creates a job that tasks run as, held by the members you choose."
			data-testid="create-form"
			@submit.prevent="openPreview"
		>
			<template #status>
				<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
			</template>
				<!-- #1757's check on Name (CTO decisions 18 and 20 on #1756): its fixed id, described by its error, then its help line. -->
				<ZhFormField :id="fields.fieldId('name')" :error-id="fields.message('name').id" label="Name" help="What the job is called." example="Account Executive">
					<template #default="{ controlId, describedBy }">
						<Input v-model="name" required v-bind="fields.bind('name')" :id="controlId" :aria-describedby="describedBy" />
					</template>
					<template #error>
						<ZhFieldError v-bind="fields.message('name')" />
					</template>
				</ZhFormField>
				<ZhFormField v-slot="{ controlId, helpId }" label="Slug" help="The short name used in links, made from the name if empty." example="account-executive">
					<Input :id="controlId" v-model="slug" placeholder="from the name if empty" :aria-describedby="helpId" />
				</ZhFormField>
				<ZhFormField label="Members" label-id="create-role-members" help="Who holds the role; a task for it goes to one of them." example="Ada" group>
					<div class="flex flex-wrap items-center gap-2">
						<TagsInput
							:model-value="holderIds"
							:display-value="memberName"
							:disabled="saving"
							class="min-w-48 flex-1"
							@remove-tag="removeMember"
						>
							<TagsInputItem v-for="member in holders" :key="member.id" :value="member.id">
								<TagsInputItemText />
								<TagsInputItemDelete tabindex="0" :aria-label="`Remove ${memberLabel(member)}`" />
							</TagsInputItem>
							<span v-if="!holderIds.length" class="text-muted-foreground px-1">No member holds this role.</span>
						</TagsInput>
						<!-- Picking a member makes them a holder at once, so the picker never holds one: it always reads "Add member…". -->
						<template v-if="availableMembers.length">
							<span id="create-role-add-member-label" class="sr-only">Add member</span>
							<!-- `aria-label` and the test id land on the field the kit Select mirrors its choice on. -->
							<Select
								:model-value="pickMember"
								:disabled="saving"
								aria-label="Add member"
								data-testid="add-role-member"
								@update:model-value="addMember"
							>
								<SelectTrigger class="min-w-40" aria-labelledby="create-role-add-member-label">
									<SelectValue placeholder="Add member…" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem v-for="member in availableMembers" :key="member.id" :value="member.id">
										{{ memberLabel(member) }}
									</SelectItem>
								</SelectContent>
							</Select>
						</template>
					</div>
					<HolderToolGaps
						:entries="holderGaps"
						section-testid="holder-tool-gaps"
						row-testid="holder-tool-gap"
						banner
						@retry="loadHolderGaps"
					/>
				</ZhFormField>
			<template #cancel>
				<Button type="button" size="sm" variant="outline" @click="router.push('/roles')">Cancel</Button>
			</template>
			<template #primary>
				<Button type="submit" size="sm" :disabled="saving">Create role</Button>
			</template>
			<template #preview>
				<ZhSavePreview v-if="preview" :lines="preview" confirm-label="Create role" :pending="saving" @confirm="confirmPreview" @edit="preview = null" />
			</template>
		</ZhFormLayout>
	</section>
</template>
